import { useState } from "react"

import { ContentObjectItem } from "@vertesia/common"
import { Download, LoaderCircle } from "lucide-react"
import mime from "mime-types"
import { toast } from "sonner"

import { isHtmlContent } from "@lib/file"
import { cn } from "@lib/utils"

import { useUserSession } from "@session/UserSession"

import { Button } from "@ui/button"

interface DownloadDocumentButtonProps {
    object: ContentObjectItem
    variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link"
    size?: "default" | "sm" | "lg" | "icon" | "xs"
    text?: string
    className?: string
}
export function DownloadDocumentButton({ object, variant, size, className, text }: Readonly<DownloadDocumentButtonProps>) {
    const { client } = useUserSession()

    const [downloading, setDownloading] = useState(false)

    const handleOnSave = () => {
        if (!object?.content?.source) {
            return
        }

        setDownloading(!downloading)
        client.objects
            .getDownloadUrl(object.content.source)
            .then(async (response) => {
                const data = await fetch(response.url)
                const blob = await data.blob()

                let text = undefined
                try {
                    text = await client.store.objects.getObjectText(object.id)
                } catch (error) {
                    console.error("Error fetching object text")
                }
                const fileExtension = text?.text && isHtmlContent(text.text) ? "html" : mime.extension(object?.content?.type || "application/octet-stream") || "bin"
                const baseName = object?.properties?.title || object.name || object.id
                const extRegex = new RegExp(`\\.${(fileExtension ?? "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`, "i")
                const nameWithoutExt = baseName.replace(extRegex, "")
                const fileName = `${nameWithoutExt}.${fileExtension}`

                const link = document.createElement("a")
                link.href = URL.createObjectURL(blob)
                link.download = fileName
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
            })
            .finally(() => {
                setDownloading(false)
            })
            .catch((error) => {
                toast.error("Error downloading document", {
                    description: error.message || "An error occurred while downloading the document.",
                    duration: 5000,
                })
            })
    }

    return (
        <Button onClick={handleOnSave} variant={variant || "outline"} size={size || "default"} className={cn(className)} disabled={downloading}>
            {downloading ? <LoaderCircle className="animate-spin motion-safe:animate-[spin_2s_linear_infinite]" /> : <Download />}
            {text && <span>{text}</span>}
        </Button>
    )
}
